import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Avatar, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Spin, 
  Empty,
  Row,
  Col,
  Divider,
  Table,
  Input,
  Tooltip,
  Select
} from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { 
  BookOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined as PendingIcon,
  MessageOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import TabListLoading from '../../../components/Common/TabListLoading';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { BootcampApplicationResponse } from '../../../types';

const { Title, Text, Paragraph } = Typography;

const BootcampRequestsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    dayjs.locale('tr');
  }, []);

  const { data, isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.bootcampApplications(
      pagination.current,
      pagination.pageSize,
      searchText || undefined,
      statusFilter || undefined
    ),
    queryFn: async () => {
      const res = await apiService.getMyBootcampApplications(
        pagination.current,
        pagination.pageSize,
        searchText || undefined,
        statusFilter || undefined
      );
      if (!res.success || !res.data) throw new Error(res.message);
      return res.data;
    },
  });

  const applications = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;

  const cancelMutation = useMutation({
    mutationFn: (requestId: number) => apiService.cancelBootcampApplication(requestId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Bootcamp talebi iptal edildi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.bootcampApplications(pagination.current, pagination.pageSize, searchText || undefined, statusFilter || undefined) });
      } else toast.error(res.message || 'Bootcamp talebi iptal edilemedi.');
    },
    onError: (err: any) => {
      if (err.response?.data?.message) toast.error(err.response.data.message);
      else toast.error('Bootcamp talebi iptal edilirken hata oluştu.');
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'orange';
      case 'Confirmed':
        return 'green';
      case 'Cancelled':
        return 'red';
      case 'Completed':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pending':
        return <PendingIcon />;
      case 'Confirmed':
        return <CheckCircleOutlined />;
      case 'Cancelled':
        return <CloseCircleOutlined />;
      case 'Completed':
        return <CheckCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Confirmed':
        return 'Onaylandı';
      case 'Cancelled':
        return 'İptal Edildi';
      case 'Completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  };

  const handleCancelRequest = (requestId: number) => cancelMutation.mutate(requestId);

  const canCancelApplication = (appliedAt: string) => {
    const applicationTime = dayjs(appliedAt);
    const now = dayjs();
    const hoursSinceApplication = now.diff(applicationTime, 'hour');
    return hoursSinceApplication < 24; // 24 saat içinde iptal edilebilir
  };

  const handleTableChange = (pag: any) => {
    setPagination(prev => ({ ...prev, current: pag.current, pageSize: pag.pageSize ?? prev.pageSize }));
  };

  const columns = [
    {
      title: 'Bootcamp',
      dataIndex: ['bootcamp', 'title'],
      key: 'bootcampTitle',
      render: (text: string, record: BootcampApplicationResponse) => (
        <Space>
          {record.bootcamp.image ? (
            <Avatar 
              src={`data:image/jpeg;base64,${record.bootcamp.image}`}
              size={40}
              style={{ borderRadius: '8px' }}
            />
          ) : (
            <Avatar icon={<BookOutlined />} style={{ backgroundColor: 'var(--primary-500)' }} size={40} />
          )}
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.bootcampId}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Başvuru Tarihi',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'İnceleme Tarihi',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: BootcampApplicationResponse) => (
        <Space>
          {record.status === 'Pending' && (
            canCancelApplication(record.appliedAt) ? (
              <Button 
                danger 
                size="small" 
                onClick={() => handleCancelRequest(record.id)}
              >
                İptal Et
              </Button>
            ) : (
              <Tooltip title="Başvuru tarihinden 24 saatten fazla geçtiği için iptal edilemez">
                <Button 
                  danger 
                  size="small" 
                  disabled
                >
                  İptal Et
                </Button>
              </Tooltip>
            )
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <TabListLoading loading={loading}>
      {/* Search and Filter Controls */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Input
            placeholder="Bootcamp adı veya not ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Durum seçin"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Select.Option value="Pending">Beklemede</Select.Option>
            <Select.Option value="Approved">Onaylandı</Select.Option>
            <Select.Option value="Rejected">Reddedildi</Select.Option>
          </Select>
        </Space>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.bootcampApplications(pagination.current, pagination.pageSize, searchText || undefined, statusFilter || undefined) })}
          loading={loading}
        >
          Yenile
        </Button>
      </div>

      {/* Table */}
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={applications}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başvuru`,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ margin: 0 }}>
              {record.coverLetter && (
                <div style={{ marginBottom: '12px' }}>
                  <Text strong>Başvuru Mektubu:</Text>
                  <br />
                  <Text>{record.coverLetter}</Text>
                </div>
              )}
              {record.mentorNotes && (
                <div>
                  <Text strong>Mentor Notları:</Text>
                  <br />
                  <Text>{record.mentorNotes}</Text>
                </div>
              )}
            </div>
          ),
          rowExpandable: (record) => !!(record.coverLetter || record.mentorNotes),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Henüz bootcamp talebiniz bulunmuyor"
            />
          ),
        }}
      />
      </TabListLoading>
    </div>
  );
};

export default BootcampRequestsTab;
